import { forwardRef } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cn } from "../../../lib/cn";

/**
 * Phase 2 — Radix DropdownMenu styled with our tokens. Replaces the
 * hand-rolled menus in the sidebar user card and AppShell's top-right
 * profile controls, plus anywhere a small menu appears on click.
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export const DropdownMenuContent = forwardRef(function DropdownMenuContent(
  { className, sideOffset = 4, ...props },
  ref,
) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-[1100] min-w-[180px] overflow-hidden rounded-lg border border-rc-blue-400",
          "bg-rc-blue-675 p-1 text-rc-text-strong shadow-rc-md",
          "data-[state=open]:animate-fadeSlide",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

export const DropdownMenuItem = forwardRef(function DropdownMenuItem({ className, inset, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        "flex cursor-pointer select-none items-center gap-2 rounded px-2.5 py-1.5",
        "text-[12.5px] text-rc-text-strong outline-none",
        "focus:bg-rc-blue-400 focus:text-white",
        "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
});

export const DropdownMenuLabel = forwardRef(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        "px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.1em] text-rc-text-dim",
        className,
      )}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = forwardRef(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn("my-1 h-px bg-rc-border", className)}
      {...props}
    />
  );
});

export function DropdownMenuShortcut({ className, ...props }) {
  return (
    <span className={cn("ml-auto text-[10.5px] text-rc-text-dim font-mono", className)} {...props} />
  );
}
